import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@lex/ui';
import { CheckIcon, LockIcon, MailIcon } from '../components/icons';
import { Orb, StatusPill } from '../components/home/primitives';
import { OtpInput } from '../components/auth/OtpInput';
import { useAuth } from '../lib/auth';
import { readPendingQuestion } from '../lib/pending-question';

type Step = 'identifier' | 'code' | 'register';

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const auth = useAuth();
  const next = params.get('next') ?? '/chat';

  const [step, setStep] = useState<Step>((params.get('step') as Step | null) ?? 'identifier');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [consentPersonalData, setConsentPersonalData] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentMedicalData, setConsentMedicalData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const pendingQuestion = useMemo(() => readPendingQuestion(), []);

  useEffect(() => {
    if (!auth.user || auth.user.registrationCompleted) return;
    setFirstName(auth.user.firstName ?? '');
    setLastName(auth.user.lastName ?? '');
    setStep('register');
  }, [auth.user]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setInterval(() => setResendIn((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const resetToIdentifier = (): void => {
    setStep('identifier');
    setCode('');
    setError(null);
  };

  const requestCode = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const result = await auth.requestEmailCode(email);
      setResendIn(result.retryAfter || 60);
      setStep('code');
      if (result.debugCode) setCode(result.debugCode);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const user = await auth.verifyEmailCode(email, code);
      if (user.registrationCompleted) {
        void navigate(next, { replace: true });
      } else {
        setFirstName(user.firstName ?? '');
        setLastName(user.lastName ?? '');
        setStep('register');
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async (): Promise<void> => {
    setError(null);
    if (!firstName.trim()) {
      setError('Укажите имя');
      return;
    }
    if (!consentPersonalData || !consentTerms) {
      setError('Отметьте обязательные согласия');
      return;
    }
    setLoading(true);
    try {
      await auth.completeRegistration({
        firstName: firstName.trim(),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        consentPersonalData,
        consentTerms,
        consentPrivacy: consentTerms,
        consentMedicalData,
      });
      void navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const title =
    step === 'code'
      ? 'Введите код'
      : step === 'register'
        ? 'Давайте познакомимся'
        : 'Войти по почте';

  const subtitle =
    step === 'code'
      ? 'Код придёт в течение минуты. Если не пришёл — запросите повторно.'
      : step === 'register'
        ? 'Осталось указать имя и подтвердить согласия — после этого можно задать вопрос юристу.'
        : 'Отправим код на почту — пароль придумывать не нужно.';

  return (
    <div className="px-4 py-10 md:py-16">
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-7">
        <div className="flex flex-col gap-2.5 text-center md:text-left">
          <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.012em] text-ink md:text-[32px]">
            {title}
          </h1>
          <p className="text-[15px] leading-[1.55] text-muted md:text-[17px]">{subtitle}</p>
        </div>

        <div className="flex flex-col gap-5 rounded-3xl border-[1.5px] border-white/90 bg-white/82 p-6 shadow-[0_16px_40px_rgba(10,28,64,0.1)] backdrop-blur-[10px] md:p-10">
          {params.get('expired') ? (
            <div className="rounded-[10px] border border-gold/40 bg-[#FBF3DE] px-4 py-3 text-sm font-medium text-[#7A5C14]">
              Сессия истекла. Войдите снова — данные и обращения сохранены.
            </div>
          ) : null}

          {pendingQuestion ? (
            <div className="flex items-center gap-2.5 rounded-[10px] bg-[linear-gradient(90deg,rgba(43,107,255,0.16)_0%,rgba(43,107,255,0.04)_100%)] px-4 py-3">
              <Orb className="h-5 w-5 shrink-0" />
              <span className="truncate text-sm font-medium text-ink">
                Ваш вопрос сохранён: после входа останется оплатить
              </span>
            </div>
          ) : null}

          {step === 'register' ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-semibold text-muted">Имя</span>
                  <input
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    placeholder="Как к вам обращаться"
                    className="rounded-2xl border border-line bg-white/95 px-4 py-3.5 text-[17px] text-ink outline-none focus:border-accent"
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-semibold text-muted">Фамилия</span>
                  <input
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    placeholder="Необязательно"
                    className="rounded-2xl border border-line bg-white/95 px-4 py-3.5 text-[17px] text-ink outline-none focus:border-accent"
                  />
                </label>
              </div>

              <div className="flex flex-col gap-3.5">
                <ConsentCheck checked={consentPersonalData} onChange={setConsentPersonalData}>
                  Согласен с{' '}
                  <Link to="/consent" className="font-semibold text-accent">
                    обработкой персональных данных
                  </Link>
                </ConsentCheck>
                <ConsentCheck checked={consentTerms} onChange={setConsentTerms}>
                  Принимаю{' '}
                  <Link to="/terms" className="font-semibold text-accent">
                    условия сервиса
                  </Link>{' '}
                  и{' '}
                  <Link to="/privacy" className="font-semibold text-accent">
                    политику конфиденциальности
                  </Link>
                </ConsentCheck>
                <ConsentCheck checked={consentMedicalData} onChange={setConsentMedicalData}>
                  Согласен на обработку медицинских данных, если они понадобятся для разбора
                  ситуации.
                </ConsentCheck>
              </div>

              {error ? <p className="text-sm text-danger">{error}</p> : null}

              <div className="flex flex-wrap items-center gap-4">
                <Button
                  variant="accent"
                  size="lg"
                  className="rounded-xl px-6"
                  loading={loading}
                  onClick={() => void completeRegistration()}
                >
                  Продолжить
                </Button>
                <button
                  type="button"
                  onClick={() => setStep('identifier')}
                  className="text-[15px] font-semibold text-accent hover:opacity-80"
                >
                  Назад
                </button>
              </div>
            </>
          ) : step === 'identifier' ? (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-ink" htmlFor="login-email">
                  Электронная почта
                </label>
                <div className="flex items-center gap-3 rounded-2xl border-2 border-accent bg-white/95 px-4 py-3.5">
                  <MailIcon className="h-5 w-5 text-muted" />
                  <input
                    id="login-email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    type="email"
                    autoComplete="email"
                    placeholder="user@example.com"
                    className="min-w-0 flex-1 bg-transparent text-[17px] text-ink outline-none placeholder:text-muted-400"
                  />
                </div>
              </div>

              <p className="text-[13px] leading-[1.45] text-muted">
                Почта нужна только для входа и уведомлений, мы никому её не передаём.
                <br />
                Впервые у нас? После кода попросим имя и согласия.
              </p>

              {error ? <p className="text-sm text-danger">{error}</p> : null}

              <div className="flex flex-wrap items-center gap-4">
                <Button
                  variant="accent"
                  size="lg"
                  className="rounded-xl px-6"
                  loading={loading}
                  onClick={() => void requestCode()}
                >
                  Получить код
                </Button>
              </div>
            </>
          ) : (
            <>
              <StatusPill tone="blue" className="w-fit">
                Код отправлен на {email}
              </StatusPill>

              <OtpInput value={code} onChange={setCode} onComplete={() => void verifyCode()} />

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">
                  {resendIn > 0 ? `Повторно через ${resendIn} с` : 'Код не пришёл?'}
                </span>
                <button
                  type="button"
                  disabled={resendIn > 0}
                  onClick={() => void requestCode()}
                  className="font-semibold text-accent disabled:opacity-50"
                >
                  Отправить снова
                </button>
              </div>

              {error ? <p className="text-sm text-danger">{error}</p> : null}

              <div className="flex flex-wrap items-center gap-4">
                <Button
                  variant="accent"
                  size="lg"
                  className="rounded-xl px-6"
                  loading={loading}
                  disabled={code.length < 4}
                  onClick={() => void verifyCode()}
                >
                  Войти
                </Button>
                <button
                  type="button"
                  onClick={resetToIdentifier}
                  className="text-[15px] font-semibold text-accent hover:opacity-80"
                >
                  Изменить почту
                </button>
              </div>
            </>
          )}

          <div className="flex items-center gap-2 border-t border-line pt-4 text-[13px] text-muted">
            <LockIcon className="h-4 w-4 shrink-0" />
            <span>Данные под защитой. Доступ к переписке — только у вас и юриста.</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-5 text-sm md:justify-start">
          <Link to="/" className="font-semibold text-accent hover:opacity-80">
            На главную
          </Link>
          <Link to="/terms" className="font-semibold text-accent hover:opacity-80">
            Условия
          </Link>
          <Link to="/privacy" className="font-semibold text-accent hover:opacity-80">
            Конфиденциальность
          </Link>
        </div>
      </div>
    </div>
  );
}

function ConsentCheck({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-start gap-3 text-[15px] leading-[22px] text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border transition-colors ${
          checked ? 'border-accent bg-accent text-white' : 'border-line bg-white text-transparent'
        }`}
      >
        <CheckIcon className="h-3.5 w-3.5" />
      </span>
      <span className="flex-1">{children}</span>
    </label>
  );
}
